/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react'
import { Layout } from './Layout'
import { t } from '../i18n/i18n'
import { Button, Tab, makeStyles } from '@fluentui/react-components'
import { BookAdd24Regular } from '@fluentui/react-icons'
import { ComplianceTabs, deleteDemandAssessment, getDemandAssessmentsColumns, getComplianceRelatedProperties, isDemandAssessmentOwnerOrCreator } from '../helpers/demandAssessmentHelper'
import { ModalDemandAssessment } from '../components/compliance/ModalDemandAssessment'
import { DemandAnalysisStatus, Workplace } from '../api/schemas/schema'
import { api } from '../helpers/apiHelper'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Overview } from '../components/common/overview/Overview'
import { IComplianceState } from '../interfaces/IComplianceState'
import { DataContext } from '../context/DataContext'
import { MoreOptionsMenuButton } from '../components/common/other/MoreOptionsMenuButton'
import { NavigationLink } from '../interfaces/INavigationLink'
import { isSystemAdmin } from '../helpers/permissionHelper'

const useStyles = makeStyles({
   addBtn: {
      '@media screen and (max-width: 640px)': {
         display: 'inline-flex',
      },
   },
   row: {
      '& >': {
         ':nth-child(1)': {
            width: '50%',
         },
         ':nth-child(2)': {
            width: '20%',
         },
         ':nth-child(3)': {
            width: '30%',
         },
      },
   },
})

export const Compliance = () => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const { organisation, userAccount } = dataContext.state
   const [searchParams, setSearchParams] = useSearchParams()
   const selectedTab = searchParams.get('tab')

   const [complianceState, setComplianceState] = useState<IComplianceState>({
      loading: true,
      allDemandAssessments: [],
      filteredDemandAssessments: [],
      open: false,
      fetchDone: false,
      leadershipPerspectives: [],
      processes: [],
      demandComplianceModules: [],
      subjects: [],
      demandAnalysisStatuses: [],
   })
   const [workplaces, setWorkplaces] = useState<Workplace[]>([])

   const navigate = useNavigate()
   useEffect(() => {
      if (!selectedTab) {
         setSearchParams({ tab: ComplianceTabs.Ongoing })
         return
      }
      if (!organisation?.id) return
      const ac = new AbortController()
      ;(async () => {
         try {
            const [assessmentsRes, workplacesRes] = await Promise.all([
               api().getDemandAnalysisByOrganisationId(organisation.id, { signal: ac.signal }),
               api().getWorkplacesByOrganisationId(organisation.id, { signal: ac.signal }),
            ])
            const allDemandAssessments = assessmentsRes.data ?? []
            const filteredDemandAssessments =
               selectedTab === ComplianceTabs.Ongoing
                  ? allDemandAssessments.filter((x) => x.status !== DemandAnalysisStatus.Completed)
                  : allDemandAssessments.filter((x) => x.status === DemandAnalysisStatus.Completed)
            setWorkplaces(workplacesRes.data)
            setComplianceState((prev) => ({ ...prev, allDemandAssessments, filteredDemandAssessments, loading: false }))
         } catch (error: any) {
            if (error?.name !== 'AbortError') throw error
         }
      })()
      return () => ac.abort()
   }, [selectedTab, dataContext.state.userAccount?.id, organisation?.id])

   useEffect(() => {
      if (selectedTab === ComplianceTabs.Ongoing)
         setComplianceState((prev) => ({
            ...prev,
            filteredDemandAssessments: prev.allDemandAssessments.filter((x) => x.status !== DemandAnalysisStatus.Completed),
         }))
      if (selectedTab === ComplianceTabs.Concluded)
         setComplianceState((prev) => ({
            ...prev,
            filteredDemandAssessments: prev.allDemandAssessments.filter((x) => x.status === DemandAnalysisStatus.Completed),
         }))
   }, [selectedTab, complianceState.allDemandAssessments.length])

   useEffect(() => {
      if (complianceState.open && !complianceState.fetchDone) {
         getComplianceRelatedProperties(dataContext, setComplianceState)
      }
   }, [complianceState.open])

   return (
      <Layout
         activeDesktopLink={NavigationLink.ComplianceOfDemands}
         breadCrumbItems={[{ title: t('ComplianceOfDemands'), active: true, path: NavigationLink.ComplianceOfDemands }]}
         tabs={
            <>
               <Tab value={ComplianceTabs.Ongoing}>{t('Ongoing')}</Tab>
               <Tab value={ComplianceTabs.Concluded}>{t('Concluded')}</Tab>
            </>
         }
         button={
            <Button
               className={classes.addBtn}
               icon={<BookAdd24Regular />}
               appearance="primary"
               onClick={() => setComplianceState((prev) => ({ ...prev, open: true }))}
            >
               {t('NewDemandAssessment')}
            </Button>
         }
      >
         <Overview
            loading={complianceState.loading}
            columns={getDemandAssessmentsColumns(setComplianceState, userAccount)}
            items={complianceState.filteredDemandAssessments}
            onRowClick={(item) => navigate(`/compliance/${item.id}`)}
            keyValue="name"
            noItemsText={selectedTab === ComplianceTabs.Ongoing ? t('TimeToCreateYourFirstDemandAssessment') : t('NoConcludedDemandAssessments')}
            title={selectedTab === ComplianceTabs.Ongoing ? t('OngoingDemandAssessments') : t('ConcludedDemandAssessments')}
            rowClassName={classes.row}
            renderMenuButton={(item) => (
               <MoreOptionsMenuButton
                  item={item}
                  disabled={!isDemandAssessmentOwnerOrCreator(item, userAccount) && !isSystemAdmin(userAccount)}
                  disabledToolTipMessage={t('DeleteDemandAssessmentNoAccessMessage')}
                  setState={setComplianceState}
                  deleteMessage={t('DeleteDemandAssessment')}
                  deleteConfirmationMessage={t('DeleteDemandAssessmentConfirmationMessage')}
                  deleteFunction={deleteDemandAssessment}
               />
            )}
         />

         <ModalDemandAssessment setComplianceState={setComplianceState} complianceState={complianceState} workplaces={workplaces} />
      </Layout>
   )
}
